import {
  Component,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  Inject,
  NgZone,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';

/**
 * ANGULAR 9 STRATEGY — read this before touching the loader below.
 *
 * We deliberately do NOT `npm install @elevenlabs/client` and `import` it.
 * Angular 9's CLI builds on webpack 4, which predates solid support for
 * package.json "exports" maps and can fail to even *parse* modern syntax
 * (optional chaining, etc.) inside node_modules, since webpack 4 doesn't
 * transpile dependencies by default. That's a build-time failure with no
 * clean fix short of upgrading Angular or patching in Babel via
 * @angular-builders/custom-webpack.
 *
 * Instead, the SDK is loaded at RUNTIME as a native ES module <script> tag,
 * pointed at jsDelivr's `+esm` endpoint. The browser's own module loader
 * parses and executes it — Angular's build never sees it, so there's
 * nothing for webpack 4 to choke on. The tradeoff: no compile-time types
 * on `Conversation` (it's `any`), and the SDK must load over the network
 * before openAssistant() can run — handled below with a cached promise.
 */
const ELEVENLABS_SDK_URL = 'https://cdn.jsdelivr.net/npm/@elevenlabs/client/+esm';
const SDK_READY_EVENT = 'trinka-elevenlabs-sdk-ready';
const SDK_LOAD_TIMEOUT_MS = 8000;

/**
 * UI states the template renders. Note there is no native "thinking" mode
 * from the SDK — see handleModeChange()/handleVadScore() for how it's derived.
 */
type AssistantState =
  | 'closed'       // nothing on screen but the FAB
  | 'connecting'   // session requested, websocket/webrtc not yet open
  | 'idle'         // connected, waiting for the user to speak
  | 'listening'    // agent mode === 'listening' and user is actively talking
  | 'thinking'     // agent mode === 'listening' but user has gone quiet (derived)
  | 'speaking'     // agent mode === 'speaking'
  | 'minimized';   // sheet hidden, session still alive in the background

interface TranscriptLine {
  source: 'user' | 'ai';
  text: string;
}

const SILENCE_MS_BEFORE_THINKING = 550; // how long of low VAD score before we call it "thinking"
const VAD_SILENCE_THRESHOLD = 0.15;      // 0–1 scale returned by onVadScore

@Component({
  selector: 'app-trinka-voice-assistant',
  templateUrl: './trinka-voice-assistant.component.html',
  styleUrls: ['./trinka-voice-assistant.component.scss'],
})
export class TrinkaVoiceAssistantComponent implements OnInit, OnDestroy {
  environment = environment;
  isMobile = false;
  @Input() agentId!: string;

  state: AssistantState = 'closed';
  isMuted = false;
  errorMessage: string | null = null;

  transcript: TranscriptLine[] = [];

  /** 0–1 live amplitude, kept as a fallback / for the ambient glow intensity. */
  inputLevel = 0;
  outputLevel = 0;

  /** Per-bar 0–1 values sampled from real frequency data, for the radial waveform. */
  readonly barCount = 24;
  audioBars: number[] = new Array(this.barCount).fill(0.08);

  private conversation: any = null; // untyped: loaded at runtime, not via npm — see note above
  private levelPollHandle: number | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Cached across calls/instances so the SDK is only fetched once per page load. */
  private static sdkLoadPromise: Promise<any> | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth <= 768;
    }
  }

  ngOnDestroy(): void {
    this.teardownSession();
  }

  // ---------------------------------------------------------------------
  // FAB tap → request mic permission → open session
  // ---------------------------------------------------------------------
  async openAssistant(): Promise<void> {
    if (this.state !== 'closed' && this.state !== 'minimized') return;

    if (this.state === 'minimized' && this.conversation) {
      // Session already running in the background — just re-show the sheet.
      this.setState('idle');
      return;
    }

    this.errorMessage = null;
    this.setState('connecting');

    try {
      // Ask up front so the browser's permission prompt appears inside a
      // clear, branded moment rather than silently inside startSession().
      console.log('isSecureContext:', window.isSecureContext);
      console.log('navigator:', navigator);
      console.log('mediaDevices:', navigator.mediaDevices);

      if (!navigator.mediaDevices) {
        throw new Error(
          'navigator.mediaDevices is undefined. The page is not running in a secure context.'
        );
      }

      await navigator.mediaDevices.getUserMedia({ audio: true });

      const ConversationCtor = await this.loadConversationSdk();

      this.conversation = await ConversationCtor.startSession({
        agentId: this.agentId,
        // 'websocket' instead of 'webrtc': avoids ElevenLabs' LiveKit transport
        // entirely, which currently has a known server-compatibility bug with
        // recent livekit-client versions ("/rtc/v1" 404s → stalled connection →
        // "Unknown DataChannel error on lossy"). websocket streams raw PCM
        // over a plain WebSocket instead, so there's no RTCPeerConnection,
        // no SCTP data channel, and nothing for that bug to affect.
        connectionType: 'websocket',

        onConnect: () => {
          this.zone.run(() => {
            this.setState('idle');
            this.startAudioLevelPolling();
          });
        },

        onDisconnect: () => {
          this.zone.run(() => this.teardownSession());
        },

        onMessage: (message: any) => {
          this.zone.run(() => {
            // message.source is 'user' | 'ai'; message.message is the text.
            // Written without optional chaining (?.) since Angular 9.0 ships
            // TypeScript 3.6, and ?. needs 3.7+.
            if (message && message.message) {
              this.pushTranscript(message.source === 'user' ? 'user' : 'ai', message.message);
            }
          });
        },

        onModeChange: ({ mode }: { mode: 'speaking' | 'listening' }) => {
          this.zone.run(() => this.handleModeChange(mode));
        },

        onVadScore: ({ vadScore }: { vadScore: number }) => {
          // Used only to derive the "thinking" state — see below.
          this.handleVadScore(vadScore);
        },

        onError: (message: string) => {
          this.zone.run(() => {
            this.errorMessage = 'Something went wrong. Please try again.';
            console.error('ElevenLabs conversation error:', message);
          });
        },
      });
    } catch (err) {
      this.zone.run(() => {
        this.errorMessage =
          err && err.message === 'ELEVENLABS_SDK_LOAD_FAILED'
            ? 'Voice assistant could not load. Check your connection and try again.'
            : 'Microphone access is needed to talk to Trinka AI.';
        this.setState('closed');
      });
      console.error(err);
    }
  }

  // ---------------------------------------------------------------------
  // Runtime SDK loader (see the strategy note at the top of this file)
  // ---------------------------------------------------------------------
  private loadConversationSdk(): Promise<any> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.reject(new Error('ELEVENLABS_SDK_LOAD_FAILED'));
    }

    const w = window as any;
    if (w.__trinkaElevenLabsConversation) {
      return Promise.resolve(w.__trinkaElevenLabsConversation);
    }

    if (TrinkaVoiceAssistantComponent.sdkLoadPromise) {
      return TrinkaVoiceAssistantComponent.sdkLoadPromise;
    }

    TrinkaVoiceAssistantComponent.sdkLoadPromise = new Promise((resolve, reject) => {
      let settled = false;

      const onReady = () => {
        if (settled) return;
        settled = true;
        window.removeEventListener(SDK_READY_EVENT, onReady);
        if (w.__trinkaElevenLabsConversation) {
          resolve(w.__trinkaElevenLabsConversation);
        } else {
          reject(new Error('ELEVENLABS_SDK_LOAD_FAILED'));
        }
      };

      window.addEventListener(SDK_READY_EVENT, onReady);

      const script = document.createElement('script');
      script.type = 'module';
      // Inline module script: the browser's native ES module loader parses
      // this, not Angular's webpack build, so modern syntax in the SDK is
      // not a build-time concern here.
      script.textContent =
        'import { Conversation } from "' + ELEVENLABS_SDK_URL + '";' +
        'window.__trinkaElevenLabsConversation = Conversation;' +
        'window.dispatchEvent(new Event("' + SDK_READY_EVENT + '"));';
      script.onerror = () => {
        if (settled) return;
        settled = true;
        window.removeEventListener(SDK_READY_EVENT, onReady);
        reject(new Error('ELEVENLABS_SDK_LOAD_FAILED'));
      };
      document.body.appendChild(script);

      setTimeout(() => {
        if (settled) return;
        settled = true;
        window.removeEventListener(SDK_READY_EVENT, onReady);
        reject(new Error('ELEVENLABS_SDK_LOAD_FAILED'));
      }, SDK_LOAD_TIMEOUT_MS);
    }).catch((err) => {
      // Don't leave a rejected promise cached — allow retry on next tap.
      TrinkaVoiceAssistantComponent.sdkLoadPromise = null;
      throw err;
    });

    return TrinkaVoiceAssistantComponent.sdkLoadPromise;
  }

  // ---------------------------------------------------------------------
  // Mode / thinking derivation
  // ---------------------------------------------------------------------
  private handleModeChange(mode: 'speaking' | 'listening'): void {
    this.clearSilenceTimer();

    if (mode === 'speaking') {
      this.setState('speaking');
    } else {
      // mode === 'listening': could be genuinely listening, or it could be
      // the gap while the agent is generating a response (the SDK doesn't
      // expose a distinct "processing" mode). We arm a short timer — if
      // the VAD score stays low past SILENCE_MS_BEFORE_THINKING we treat
      // it as "thinking" until either speech resumes or the agent replies.
      this.setState('listening');
      this.armThinkingWatch();
    }
  }

  private handleVadScore(vadScore: number): void {
    if (vadScore > VAD_SILENCE_THRESHOLD) {
      // User is actively talking — cancel any pending "thinking" flip and
      // make sure we're showing "listening".
      this.clearSilenceTimer();
      if (this.state === 'thinking') {
        this.zone.run(() => this.setState('listening'));
      }
    } else {
      this.armThinkingWatch();
    }
  }

  private armThinkingWatch(): void {
    if (this.silenceTimer || this.state === 'speaking' || this.state === 'thinking') return;
    this.silenceTimer = setTimeout(() => {
      this.zone.run(() => {
        if (this.state === 'listening') this.setState('thinking');
      });
    }, SILENCE_MS_BEFORE_THINKING);
  }

  private clearSilenceTimer(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  // ---------------------------------------------------------------------
  // Live waveform amplitude — real audio data, not decorative CSS only
  // ---------------------------------------------------------------------
  private startAudioLevelPolling(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const poll = async () => {
      if (!this.conversation) return;
      try {
        const isSpeaking = this.state === 'speaking';
        const freqData = isSpeaking
          ? this.conversation.getOutputByteFrequencyData()
          : this.conversation.getInputByteFrequencyData();

        this.audioBars = this.sampleToBars(freqData, this.barCount);
        this.inputLevel = await this.conversation.getInputVolume();
        this.outputLevel = await this.conversation.getOutputVolume();
      } catch {
        // no-op: throws briefly during connect/disconnect transitions
      }
      this.levelPollHandle = requestAnimationFrame(poll);
    };
    this.levelPollHandle = requestAnimationFrame(poll);
  }

  /** Downsamples a Uint8Array frequency buffer into N normalized (0–1) bar heights. */
  private sampleToBars(data: Uint8Array, bars: number): number[] {
    if (!data || data.length === 0) return new Array(bars).fill(0.08);
    const chunk = Math.floor(data.length / bars) || 1;
    const out: number[] = [];
    for (let i = 0; i < bars; i++) {
      let sum = 0;
      const start = i * chunk;
      for (let j = start; j < start + chunk && j < data.length; j++) sum += data[j];
      const avg = sum / chunk / 255; // normalize 0–1
      out.push(Math.max(0.08, avg)); // floor so idle bars stay visible
    }
    return out;
  }

  private stopAudioLevelPolling(): void {
    if (this.levelPollHandle !== null) {
      cancelAnimationFrame(this.levelPollHandle);
      this.levelPollHandle = null;
    }
  }

  // ---------------------------------------------------------------------
  // Controls
  // ---------------------------------------------------------------------
  toggleMute(): void {
    if (!this.conversation) return;
    this.isMuted = !this.isMuted;
    this.conversation.setMicMuted(this.isMuted);
  }

  minimize(): void {
    // Session stays alive; only the sheet UI hides.
    this.setState('minimized');
  }

  async endCall(): Promise<void> {
    await this.teardownSession();
  }

  private async teardownSession(): Promise<void> {
    this.stopAudioLevelPolling();
    this.clearSilenceTimer();
    if (this.conversation) {
      try {
        await this.conversation.endSession();
      } catch {
        /* already closed */
      }
      this.conversation = null;
    }
    this.transcript = [];
    this.isMuted = false;
    this.setState('closed');
  }

  private pushTranscript(source: 'user' | 'ai', text: string): void {
    this.transcript.push({ source, text });
    // Keep only the last few lines visible — this is a live caption strip,
    // not a full chat history.
    if (this.transcript.length > 6) this.transcript.shift();
  }

  private setState(state: AssistantState): void {
    this.state = state;
    this.cdr.markForCheck();
  }

  get latestLine(): TranscriptLine | null {
    return this.transcript.length ? this.transcript[this.transcript.length - 1] : null;
  }
}

